import { useEffect, useState } from 'react';
import axios, { AxiosInstance } from 'axios';
import { useSnackbar } from 'notistack';
import { get } from 'lodash';

import { formDataInstance } from '@/common/axios';
import CONFIG from '@/config';
import { useLock } from '@/context/LockContext';
import AuthUser from '@/common/utils/AuthUser';
import { isAtLeastManager } from '@/models/common';

export enum ELEVATED_ROLE_ACTIONS {
  REFUND = 'refund',
  ADD_CREDITS = 'add_credits',
  EXTEND_CREDITS = 'extend_credits',
  EXPIRE_CREDITS = 'expire_credits',
  EDIT_LINE_ITEM_COMMISSION = 'edit_line_item_commission'
}

export interface elevatedActionInfo {
  isReady: boolean;
  transport: AxiosInstance;
}

export interface useElevatedActionProps {
  actionType: ELEVATED_ROLE_ACTIONS;
  onCancel?: () => void;
  promptMessage?: string;
  serviceApiUrl?: string;
}

// https://github.com/facebook/react/issues/31389 is preventing storage of the axios instance in useState, so
let elevatedActionXHR: AxiosInstance | null = null;

export const ApiErrorLookup = {
  UNKNOWN: 'An unknown error occurred.',
  INVALID_PIN: 'The pin provided is invalid.',
  UNAUTHORIZED: 'The pin provided is not authorized for this action.',
  INVALID_AUTH_USER: 'The pin provided is not for a valid user.'
};

export const getErrorName = error => get(error, 'response.data.error_code');

export const getErrorDetails = (error: Error) =>
  ApiErrorLookup[getErrorName(error)] || ApiErrorLookup.UNKNOWN;

export const elevatedActionApiSuffix = '/auth/staff/elevated-action';

export const userHasAtLeastManagerRole = () => {
  const roles = AuthUser.getRoles();
  return isAtLeastManager(roles);
};

// if the current user session is not at the manager role or above it prompts the user for a manager pin and if successful configures an axios instance
export const useElevatedRoleAction = (
  props: useElevatedActionProps
): elevatedActionInfo => {
  const { actionType, onCancel, promptMessage, serviceApiUrl } = props;

  const [isReady, setIsReady] = useState<boolean>(false);
  const { enqueueSnackbar } = useSnackbar();
  const { withPinEntry } = useLock();

  const askForPin = async () => {
    withPinEntry(async (pin: string, cancelled: boolean) => {
      if (cancelled) {
        onCancel?.();
        return;
      } else {
        await formDataInstance
          .post(
            CONFIG.USERS_API_URL + elevatedActionApiSuffix,
            {
              pin,
              action: actionType
            },
            {
              headers: {
                'Content-Type': 'application/json; charset=utf-8'
              }
            }
          )
          .then(response => {
            const oneTimeTransport = axios.create({
              baseURL: serviceApiUrl || CONFIG.API,
              timeout: 30000,
              headers: {
                'Content-Type': 'json/application'
              }
            });

            oneTimeTransport.interceptors.request.use(
              request => {
                request.headers.Authorization = `JWT ${response.data.data.token}`;
                return request;
              },
              error => {
                return Promise.reject(error);
              }
            );

            /* seriously just a one time use
            oneTimeTransport.interceptors.response.use(
              response => {
                elevatedActionXHR = null;
                setIsReady(false);
                return response;
              },
              error => {
                return Promise.reject(error);
              }
            );*/
            elevatedActionXHR = oneTimeTransport;
            setIsReady(true);
            enqueueSnackbar('Pin Success', { variant: 'success' });
          })
          .catch(e => {
            enqueueSnackbar(getErrorDetails(e), { variant: 'error' });
            console.error(e);
            props?.onCancel?.();
          });
      }
    }, promptMessage);
  };

  useEffect(() => {
    const hasAtLeastManagerRole = userHasAtLeastManagerRole();
    if (hasAtLeastManagerRole) {
      // if the user already has elevated auth, use the standard configured axios instance
      elevatedActionXHR = formDataInstance;
      setIsReady(true);
    } else {
      // otherwise get an elevated priv via pin and configure a onetime use axios instance with the token
      askForPin();
    }

    // cleaup on component dismount
    return () => {
      elevatedActionXHR = null;
    };
  }, []);

  return {
    isReady,
    transport: elevatedActionXHR
  };
};
